import { getallpartner, searchPartner } from "../services/partner/partner";
import { getallsupplier, fetchShipFroms } from "../services/supplier/supplier";
import { getallcustomer, fetchShipTos } from "../services/customer/customer";

const partnerTypeLabel = (p) => {
  const t = p?.partnerTypeDetails;
  if (Array.isArray(t)) return t.map((x) => x?.value).filter(Boolean).join(", ");
  return t?.value ?? p?.partner_type ?? "";
};

const statusLabel = (s) => {
  const v = String(s ?? "").trim().toLowerCase();
  if (v === "1" || v === "active") return "Active";
  if (v === "2" || v === "pending") return "Pending";
  if (v === "3" || v === "inactive") return "Inactive";
  return String(s ?? "");
};

/** Columns for the supplier / customer-group list rows (`<prefix>_id`, `<prefix>_name`, nested `address`). */
const storerColumns = (prefix, label) => [
  { key: "id", label: `${label} ID`, get: (r) => r?.[`${prefix}_id`] },
  { key: "name", label: `${label} Name`, get: (r) => r?.[`${prefix}_name`] },
  { key: "email", label: "Email", get: (r) => r?.[`${prefix}_email`] },
  { key: "mobile", label: "Mobile", get: (r) => r?.[`${prefix}_mobile`] },
  { key: "city", label: "City", get: (r) => r?.address?.[`${prefix}_city`] },
  { key: "state", label: "State", get: (r) => r?.address?.[`${prefix}_state`] },
  { key: "country", label: "Country", get: (r) => r?.address?.[`${prefix}_country`] },
  { key: "status", label: "Status", get: (r) => statusLabel(r?.status) },
];

/**
 * Flatten a supplier / customer-group record to one shape so each module
 * can map it onto its own address field names.
 */
export const normalizeStorer = (rec, prefix) => {
  const addr = rec?.address || {};
  return {
    _id: rec?._id ?? "",
    organizationCustomerId:
      rec?.organizationCustomerId?._id ?? rec?.organizationCustomerId ?? rec?._id ?? "",
    id: rec?.[`${prefix}_id`] ?? "",
    name: rec?.[`${prefix}_name`] ?? "",
    email: rec?.[`${prefix}_email`] ?? "",
    mobile: rec?.[`${prefix}_mobile`] ?? "",
    address1: addr?.[`${prefix}_address1`] ?? "",
    address2: addr?.[`${prefix}_address2`] ?? "",
    city: addr?.[`${prefix}_city`] ?? "",
    state: addr?.[`${prefix}_state`] ?? "",
    country: addr?.[`${prefix}_country`] ?? "",
    pincode: addr?.[`${prefix}_pincode`] ?? addr?.[`${prefix}_postCode`] ?? "",
    raw: rec,
  };
};

/** Partner records (`partner/fetch`). */
export const PARTNER_LOOKUP = {
  title: "Select Partner",
  idKey: "partner_id",
  placeholder: "Search by Partner ID or Name",
  columns: [
    { key: "partner_id", label: "Partner ID", get: (p) => p?.partner_id },
    { key: "partner_name", label: "Partner Name", get: (p) => p?.partner_name },
    { key: "partner_email", label: "Email", get: (p) => p?.partner_email },
    { key: "partner_mobile", label: "Mobile", get: (p) => p?.partner_mobile },
    { key: "partner_type", label: "Partner Type", get: partnerTypeLabel },
    { key: "city", label: "City", get: (p) => p?.address?.partner_city },
    { key: "status", label: "Status", get: (p) => p?.status },
  ],
  fetchPage: (page, pageSize) => getallpartner(page, pageSize),
  search: async (q) => {
    const res = await searchPartner(q);
    return Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
  },
};

/** Supplier records (`supplier/fetch-multi-dynamic`). */
export const SUPPLIER_LOOKUP = {
  title: "Select Supplier",
  idKey: "supplier_id",
  placeholder: "Search by Supplier ID or Name",
  columns: storerColumns("supplier", "Supplier"),
  fetchPage: (page, pageSize) => getallsupplier(page, pageSize),
  searchKeys: ["supplier_id", "supplier_name"],
  normalize: (rec) => normalizeStorer(rec, "supplier"),
};

/** Customer-group records (`customer-group/fetch-multi-dynamic`). */
export const CUSTOMER_LOOKUP = {
  title: "Select Customer",
  idKey: "customer_id",
  placeholder: "Search by Customer ID or Name",
  columns: storerColumns("customer", "Customer"),
  fetchPage: (page, pageSize) => getallcustomer(page, pageSize),
  searchKeys: ["customer_id", "customer_name"],
  normalize: (rec) => normalizeStorer(rec, "customer"),
};

/**
 * Ship To records for one customer (`customer-group/ship-to/fetch-multi-dynamic`).
 * `customerId` is the customer record's mongo `_id`. Without it the list stays empty.
 */
export const shipToLookupForCustomer = (customerId) => ({
  title: "Select Ship To",
  idKey: "shipto_id",
  placeholder: "Search by Ship To ID or Name",
  columns: storerColumns("shipto", "Ship To"),
  fetchPage: (page, pageSize) =>
    customerId
      ? fetchShipTos(page, pageSize, { customerId })
      : Promise.resolve({ data: [], total: 0 }),
  searchKeys: ["shipto_id", "shipto_name"],
  normalize: (rec) => normalizeStorer(rec, "shipto"),
});

/**
 * Ship From records for one supplier (`supplier/ship-from/fetch-multi-dynamic`).
 * `supplierId` is the supplier record's mongo `_id`. Without it the list stays empty.
 */
export const shipFromLookupForSupplier = (supplierId) => ({
  title: "Select Ship From",
  idKey: "shipfrom_id",
  placeholder: "Search by Ship From ID or Name",
  columns: storerColumns("shipfrom", "Ship From"),
  fetchPage: (page, pageSize) =>
    supplierId
      ? fetchShipFroms(page, pageSize, { supplierId })
      : Promise.resolve({ data: [], total: 0 }),
  searchKeys: ["shipfrom_id", "shipfrom_name"],
  normalize: (rec) => normalizeStorer(rec, "shipfrom"),
});
